import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { SwingUploadShotContext } from "@/components/mvp/SwingUploadShotContext";
import type { ShotIssueTag } from "@/types/shotIssues";
import { AlertCircle, Check, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useDirectUpload } from "@/features/upload/useDirectUpload";
import { useClientPoseExtraction } from "@/features/upload/useClientPoseExtraction";
import { BiometricConsentGate } from "@/components/consent/BiometricConsentGate";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { usePipelineDiagnostics } from "@/hooks/usePipelineDiagnostics";

interface AnalysisStepConfig {
  id: string;
  label: string;
  description: string;
}

const ANALYSIS_STEPS: AnalysisStepConfig[] = [
  { id: 'upload', label: 'Uploading Video', description: 'Sending your video to the cloud...' },
  { id: 'pose', label: 'Pose Extraction', description: 'Breaking down your swing frame by frame...' },
  { id: 'analysis', label: 'AI Analysis', description: 'Our coaching engine is evaluating your mechanics...' },
  { id: 'report', label: 'Generating Report', description: 'Preparing your personalized results...' },
];

function getActiveStepIndex(isUpload: boolean, isPose: boolean, status: string | undefined, stage: string | undefined): number {
  if (isUpload) return 0;
  if (isPose) return 1;
  if (status === 'ready') return 4;
  if (status === 'failed') return -1;
  switch (stage) {
    case 'telemetry':
    case 'analysis':
      return 2;
    case 'complete':
      return 4;
    default:
      return 2;
  }
}

const FUN_FACTS = [
  'The average recreational golfer loses 12+ yards from swing inefficiencies that are easy to fix with targeted practice.',
  'Most swing faults can be improved by 40% within just 2-4 weeks of focused drill work.',
  'AI swing analysis can detect movement patterns invisible to the naked eye.',
  'Tour pros practice specific drills for only 15-20 minutes per session — quality over quantity.',
];

const PIPELINE_TYPE_V3 = 'v3' as const;

export default function UploadScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getDirectUrl, uploadToCloudflare, insertRun } = useDirectUpload();
  const { extractPose, progress: poseProgress } = useClientPoseExtraction();
  const { recordStage } = usePipelineDiagnostics();
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessingPose, setIsProcessingPose] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadedRunId, setUploadedRunId] = useState<string | null>(null);
  const [uploadedCfUid, setUploadedCfUid] = useState<string | null>(null);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showBiometricConsent, setShowBiometricConsent] = useState(false);
  const [hasBiometricConsent, setHasBiometricConsent] = useState<boolean | null>(null);
  
  // Shot context state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [shotContextTags, setShotContextTags] = useState<ShotIssueTag[]>([]);
  const [shotContextNotes, setShotContextNotes] = useState<string | null>(null);

  const [funFact] = useState(() => FUN_FACTS[Math.floor(Math.random() * FUN_FACTS.length)]);
  const hasNavigatedRef = useRef(false);

  const { data: runStatus } = useQuery({
    queryKey: ['v3-run-status', uploadedRunId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('swing_runs')
        .select('status, analysis_stage, analysis_progress')
        .eq('id', uploadedRunId!)
        .single();
      if (error) throw error;
      return data as { status: string; analysis_stage: string | null; analysis_progress: number | null };
    },
    enabled: !isUploading && !isProcessingPose && !!uploadedRunId,
    refetchInterval: (query) => {
      const d = query.state.data;
      if (d?.status === 'ready' || d?.status === 'failed') return false;
      return 3000;
    },
  });

  const analysisComplete = runStatus?.status === 'ready';
  const analysisFailed = runStatus?.status === 'failed';
  const showProcessing = isUploading || uploadSuccess;
  const activeStepIndex = getActiveStepIndex(isUploading, isProcessingPose, runStatus?.status, runStatus?.analysis_stage ?? undefined);

  const overallProgress = useMemo(() => {
    if (analysisComplete) return 100;
    if (isUploading) return Math.round(uploadPct * 0.15);
    if (isProcessingPose) return 15 + Math.round(poseProgress.progress * 0.2);
    return runStatus?.analysis_progress ?? 40;
  }, [isUploading, uploadPct, isProcessingPose, poseProgress.progress, runStatus?.analysis_progress, analysisComplete]);

  useEffect(() => {
    if (analysisComplete && uploadedRunId && !hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      const timer = setTimeout(() => {
        navigate(`/analysis/${uploadedRunId}`);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [analysisComplete, uploadedRunId, navigate]);

  const statusText = useMemo(() => {
    if (analysisComplete) return 'Analysis Complete!';
    if (analysisFailed) return 'Analysis Failed';
    if (isUploading) return `Uploading video... ${uploadPct}%`;
    if (isProcessingPose) {
      switch (poseProgress.status) {
        case 'loading_video': return 'Loading video...';
        case 'initializing_mediapipe': return 'Initializing AI models...';
        case 'extracting': return `Extracting pose data... (${poseProgress.framesProcessed} frames)`;
        case 'saving': return 'Saving pose data...';
        case 'triggering_analysis': return 'Starting AI analysis...';
        default: return 'Preparing your video...';
      }
    }
    if (!runStatus) return 'Starting analysis...';
    switch (runStatus.analysis_stage) {
      case 'telemetry': return 'Running AI analysis...';
      case 'analysis': return 'Running AI analysis...';
      case 'complete': return 'Finalizing...';
      default: return 'Processing...';
    }
  }, [runStatus, analysisComplete, analysisFailed, isUploading, uploadPct, isProcessingPose, poseProgress.status, poseProgress.framesProcessed]);

  const timeEstimate = useMemo(() => {
    if (analysisComplete || analysisFailed) return undefined;
    if (isUploading) {
      const uploadRemaining = Math.max(2, Math.round((100 - uploadPct) * 0.1));
      return `Est. ${uploadRemaining + 45}s remaining`;
    }
    if (isProcessingPose) {
      const poseRemaining = Math.max(3, Math.round((100 - poseProgress.progress) * 0.1));
      return `Est. ${poseRemaining + 35}s remaining`;
    }
    const remaining = Math.max(5, Math.round((100 - overallProgress) * 0.5));
    return `Est. ${remaining}s remaining`;
  }, [overallProgress, analysisComplete, analysisFailed, isUploading, uploadPct, isProcessingPose, poseProgress.progress]);

  const handleRetryUpload = () => {
    setIsUploading(false);
    setUploadPct(0);
    setUploadSuccess(false);
    setIsProcessingPose(false);
    setUploadedRunId(null);
    setUploadedCfUid(null);
    setUploadError(null);
    setSelectedFile(null);
    hasNavigatedRef.current = false;
  };

  useEffect(() => {
    if (!user) return;
    const checkConsent = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('biometric_consent_at')
        .eq('user_id', user.id)
        .maybeSingle();
      setHasBiometricConsent(!!(data as any)?.biometric_consent_at);
    };
    checkConsent();
  }, [user]);

  const handleUploadStart = () => {
    console.log('[UploadScreen] Upload started');
    setIsUploading(true);
    setUploadSuccess(false);
    setUploadError(null);
  };

  const handleUploadComplete = (runId: string) => {
    console.log('[UploadScreen] ✅ Upload completed successfully with runId:', runId);
    setUploadedRunId(runId);
    setIsUploading(false);
    setUploadSuccess(true);
  };

  const handleFileSelect = (file: File) => {
    console.log('[UploadScreen] File selected:', file.name);
    setSelectedFile(file);
  };

  const handleShotContextContinue = async (tags: ShotIssueTag[], category: string | null, notes: string | null) => {
    console.log('[UploadScreen] Shot context completed:', { tags, category, notes });
    setShotContextTags(tags);
    setShotContextNotes(notes);
    
    // Start upload with shot context
    if (selectedFile) {
      await startUploadWithShotContext(selectedFile, tags, category, notes);
    }
  };

  const handleShotContextSkip = async () => {
    console.log('[UploadScreen] Shot context skipped');
    setShotContextTags([]);
    setShotContextNotes(null);
    
    // Start upload without shot context
    if (selectedFile) {
      await startUploadWithShotContext(selectedFile, [], null, null);
    }
  };

  const startUploadWithShotContext = async (file: File, tags: ShotIssueTag[], category: string | null, notes: string | null) => {
    console.log('[UploadScreen] Starting upload with shot context:', { file: file.name, tags, category, notes });

    if (!hasBiometricConsent) {
      setShowBiometricConsent(true);
      return;
    }
    
    try {
      const tUploadInit = new Date().toISOString();
      handleUploadStart();

      // Get session
      const { supabase } = await import("@/integrations/supabase/client");
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("User not authenticated");
      }

      // Step 1: Get upload URL
      const { uploadURL, uid } = await getDirectUrl(session.access_token);

      // Step 2: Upload to Cloudflare
      await uploadToCloudflare(file, uploadURL, (progress) => {
        console.log('[UploadScreen] Upload progress:', progress.pct);
        setUploadPct(progress.pct);
      });

      // Step 3: Create database record
      const runId = await insertRun(uid, file);
      console.log('[UploadScreen] ✅ Database record created, runId:', runId);

      try {
        recordStage({ runId, pipelineType: PIPELINE_TYPE_V3, stage: 'upload_initiated', clientRecordedAt: tUploadInit });
        recordStage({ runId, pipelineType: PIPELINE_TYPE_V3, stage: 'cf_upload_complete', clientRecordedAt: new Date().toISOString() });
      } catch (_) { /* non-blocking */ }

      // Step 4: Save shot context to database
      try {
        if (tags.length > 0 || notes) {
          console.log('[UploadScreen] 💾 Saving shot context...', { tags, category, notes });
          const { deriveShotIssueCategory } = await import("@/types/shotIssues");
          const derivedCategory = category || deriveShotIssueCategory(tags);
          
          const { error } = await supabase
            .from('swing_runs' as any)
            .update({
              shot_issue_tags: tags,
              shot_issue_category: derivedCategory,
              shot_issue_notes: notes,
            } as any)
            .eq('id', runId);
            
          if (error) {
            console.error('[UploadScreen] Error saving shot context:', error);
            // Don't throw - continue with upload completion
          } else {
            console.log('[UploadScreen] ✅ Shot context saved successfully');
          }
        }
      } catch (shotContextError) {
        console.error('[UploadScreen] Shot context save failed, but continuing:', shotContextError);
        // Don't throw - continue with upload completion
      }
      
      // Step 5: Transition to processing screen and start pose extraction
      console.log('[UploadScreen] Upload completed, starting pose extraction...');
      setUploadedRunId(runId);
      setUploadedCfUid(uid);
      setIsUploading(false);
      setIsProcessingPose(true);
      setUploadSuccess(true);
      
      // Step 6: Run client-side pose extraction (faster than server: ~5-8s vs ~16s)
      // Wait a few seconds for Cloudflare to finish transcoding
      console.log('[UploadScreen] Waiting for Cloudflare transcoding...');
      try {
        recordStage({ runId, pipelineType: PIPELINE_TYPE_V3, stage: 'pose_extraction_start', clientRecordedAt: new Date().toISOString() });
      } catch (_) { /* non-blocking */ }
      await new Promise(resolve => setTimeout(resolve, 5000));

      console.log('[UploadScreen] Starting client-side pose extraction...');
      const poseResult = await extractPose(uid, runId);

      if (poseResult.success) {
        try {
          recordStage({ runId, pipelineType: PIPELINE_TYPE_V3, stage: 'pose_extraction_complete', clientRecordedAt: new Date().toISOString(), meta: { framesProcessed: poseResult.framesProcessed, durationMs: poseResult.durationMs } });
          recordStage({ runId, pipelineType: PIPELINE_TYPE_V3, stage: 'pose_saved', clientRecordedAt: new Date().toISOString() });
          recordStage({ runId, pipelineType: PIPELINE_TYPE_V3, stage: 'result_available', clientRecordedAt: new Date().toISOString() });
        } catch (_) { /* non-blocking */ }
        console.log('[UploadScreen] ✅ Client-side pose extraction complete:', {
          frames: poseResult.framesProcessed,
          durationMs: poseResult.durationMs,
        });
        setIsProcessingPose(false);
      } else {
        // Pose is client-side only; server-side/GPU worker was deprecated. Do not fall back to orchestration.
        console.error('[UploadScreen] Client-side pose extraction failed:', poseResult.error);
        try {
          recordStage({ runId, pipelineType: PIPELINE_TYPE_V3, stage: 'pose_extraction_complete', clientRecordedAt: new Date().toISOString(), meta: { error: poseResult.error } });
        } catch (_) { /* non-blocking */ }
        setIsProcessingPose(false);
        setUploadSuccess(false);
        const msg = poseResult.error?.includes('Format error') || poseResult.error?.includes('MEDIA_ELEMENT_ERROR')
          ? 'This video couldn\'t be processed in your browser (format or codec). Try a different file or browser.'
          : 'Pose extraction failed. Please try again or use a different video.';
        setUploadError(msg);
      }
      
    } catch (error) {
      console.error('[UploadScreen] Upload failed:', error);
      setIsUploading(false);
      setUploadPct(0);
      setUploadSuccess(false);
      
      // Set user-friendly error message but keep the file selected
      // PLA-21: Improved error messaging to accurately reflect failure mode
      let errorMessage = 'Upload failed. Please try again.';
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch')) {
          // PLA-21: "Failed to fetch" typically means CORS or network issues
          // Log additional context to help diagnose the issue
          console.error('[UploadScreen] Failed to fetch - possible CORS or network issue:', {
            supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
            origin: window.location.origin,
          });
          errorMessage = 'Unable to connect to upload service. Please try again or contact support if the issue persists.';
        } else if (error.message.includes('User not authenticated')) {
          errorMessage = 'Authentication expired. Please refresh the page and sign in again.';
        } else if (error.message.includes('Failed to get upload URL')) {
          errorMessage = 'Server temporarily unavailable. Please try again in a moment.';
        } else if (error.message.includes('Orchestration failed') || error.message.includes('still being analyzed')) {
          errorMessage = error.message.includes('still being analyzed')
            ? error.message
            : 'Analysis could not be started. Please try again or check your dashboard in a few minutes.';
        }
      }
      setUploadError(errorMessage);
      
      // Don't clear the selected file - let user retry with same file
    }
  };

  return (
    <div className="min-h-screen pb-24 bg-white">
      {showBiometricConsent && (
        <BiometricConsentGate
          onConsentGranted={() => {
            setShowBiometricConsent(false);
            setHasBiometricConsent(true);
            if (selectedFile) {
              startUploadWithShotContext(selectedFile, shotContextTags, null, shotContextNotes);
            }
          }}
          onCancel={() => {
            setShowBiometricConsent(false);
          }}
        />
      )}
      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-4">
            <img 
              src="/nodoubles_logo_trans.png" 
              alt="No Doubles Golf Co" 
              className="h-6 sm:h-8 object-contain"
            />
            <h1 className="text-lg sm:text-xl font-semibold text-[#0D382A]">Upload & Analyze</h1>
          </div>
          <button 
            onClick={() => navigate('/mvp/home')}
            className="text-gray-600 hover:text-[#0D382A] transition-colors text-sm sm:text-base"
          >
            <span className="hidden sm:inline">← Back to Dashboard</span>
            <span className="sm:hidden">←</span>
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!showProcessing ? (
          <div className="bg-white rounded-2xl p-6 shadow-[0_2px_8px_rgba(13,56,42,0.08)]">
            <div className="w-full max-w-2xl mx-auto">
              {/* Error Message */}
              {uploadError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-800">{uploadError}</p>
                    </div>
                    <div className="ml-auto pl-3">
                      <button
                        onClick={() => setUploadError(null)}
                        className="inline-flex text-red-400 hover:text-red-600"
                      >
                        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              <SwingUploadShotContext
                selectedFile={selectedFile}
                onFileSelect={setSelectedFile}
                initialTags={shotContextTags}
                initialNotes={shotContextNotes}
                onContinue={handleShotContextContinue}
                onBack={() => setSelectedFile(null)}
                onSkip={handleShotContextSkip}
                isUploading={isUploading}
              />
            </div>
          </div>
        ) : showProcessing ? (
          <div className="max-w-lg mx-auto">
            {/* Circular progress */}
            <div className="text-center mb-8">
              <div className="relative inline-flex items-center justify-center w-32 h-32 mb-4">
                <svg className="w-32 h-32 -rotate-90" viewBox="0 0 128 128">
                  <circle cx="64" cy="64" r="56" fill="none" stroke="#e5e7eb" strokeWidth="8" />
                  <circle
                    cx="64" cy="64" r="56"
                    fill="none"
                    stroke={analysisComplete ? '#22c55e' : analysisFailed ? '#ef4444' : '#0D382A'}
                    strokeWidth="8"
                    strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 56}
                    strokeDashoffset={2 * Math.PI * 56 * (1 - (analysisComplete ? 1 : overallProgress / 100))}
                    className="transition-all duration-700 ease-out"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  {analysisComplete ? (
                    <Check className="w-10 h-10 text-green-500" />
                  ) : analysisFailed ? (
                    <AlertCircle className="w-10 h-10 text-red-500" />
                  ) : (
                    <span className="text-2xl font-bold text-[#0D382A]">{overallProgress}%</span>
                  )}
                </div>
              </div>
              <h2 className="text-xl font-bold text-[#0D382A] mb-1">{statusText}</h2>
              {timeEstimate && <p className="text-sm text-gray-500">{timeEstimate}</p>}
              {analysisComplete && (
                <p className="text-sm text-green-600 mt-1">Redirecting to your results...</p>
              )}
            </div>

            {/* Step list */}
            {!analysisFailed && (
              <div className="bg-white rounded-2xl p-5 shadow-[0_2px_8px_rgba(13,56,42,0.08)] mb-6">
                <div className="space-y-4">
                  {ANALYSIS_STEPS.map((step, idx) => {
                    const isComplete = idx < activeStepIndex;
                    const isActive = idx === activeStepIndex && !analysisComplete;
                    return (
                      <div key={step.id} className="flex items-start gap-3">
                        <div className={`flex-shrink-0 mt-0.5 w-6 h-6 rounded-full flex items-center justify-center transition-colors duration-300 ${
                          isComplete ? 'bg-green-500' : isActive ? 'bg-[#0D382A]' : 'bg-gray-200'
                        }`}>
                          {isComplete ? (
                            <Check className="w-3.5 h-3.5 text-white" />
                          ) : isActive ? (
                            <Loader2 className="w-3.5 h-3.5 text-white animate-spin" />
                          ) : (
                            <span className="w-2 h-2 rounded-full bg-gray-400" />
                          )}
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${isComplete || isActive ? 'text-[#0D382A]' : 'text-gray-400'}`}>
                            {step.label}
                          </p>
                          {isActive && (
                            <p className="text-xs text-gray-500 mt-0.5">{step.description}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Error state */}
            {analysisFailed && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-5 mb-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800">Analysis could not be completed</p>
                    <p className="text-sm text-red-600 mt-1">
                      Something went wrong during processing. You can try uploading again.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleRetryUpload}
                  variant="outline"
                  className="mt-4 w-full border-red-300 text-red-700 hover:bg-red-100"
                >
                  <RefreshCw className="w-4 h-4 mr-2" /> Try Again
                </Button>
              </div>
            )}

            {/* Fun fact card */}
            {!analysisComplete && !analysisFailed && (
              <div className="bg-gradient-to-br from-[#0D382A] to-[#1a5c46] rounded-2xl p-5 text-white">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#A7F3A1] mb-2">Did you know?</p>
                <p className="text-sm leading-relaxed opacity-90">{funFact}</p>
              </div>
            )}
          </div>
        ) : null}
      </main>
    </div>
  );
}